import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Basketball, Lock, Eye, EyeSlash } from "@phosphor-icons/react";

const BG = "/iefsc-community.png";

export default function AdminLogin() {
  const { admin, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (admin && admin !== false) navigate("/admin/dashboard");
  }, [admin, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Admin");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative px-6">
      <img src={BG} alt="IEFSC" className="absolute inset-0 w-full h-full object-contain bg-black" />
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative w-full max-w-md border border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl p-10" data-testid="admin-login-card">
        <div className="flex items-center gap-3 mb-8">
          <img src="/iefsc-logo.png" alt="IEFSC" className="w-12 h-12 object-contain" />
          <span className="font-display text-4xl text-[#FF5722]">IEFSC</span>
        </div>
        <p className="text-xs tracking-[0.25em] uppercase text-[#FF5722] mb-2 font-semibold flex items-center gap-2"><Lock size={14} weight="bold" /> Admin Access</p>
        <h1 className="font-display text-4xl uppercase mb-8">Control Room</h1>

        <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
          <div>
            <label className="block text-sm font-semibold uppercase tracking-wide text-white/70 mb-2">Email</label>
            <input
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#1A1A1A] border border-white/15 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold uppercase tracking-wide text-white/70 mb-2">Password</label>
            <div className="relative">
              <input
                data-testid="login-password-input"
                type={showPw ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#1A1A1A] border border-white/15 px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                aria-label={showPw ? "Hide password" : "Show password"}
                data-testid="login-password-toggle"
                className="absolute inset-y-0 right-0 flex items-center px-4 text-white/40 hover:text-[#FF5722] transition-colors"
              >
                {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="w-full bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-4 transition-all duration-200"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
